import type { ReportMetadata, VorankuendigungArchiveEntry, VorankuendigungData } from '../types';
import { stabilerInhaltsHash } from './ablageHilfen';
import { describeVorankuendigungVersion, formatVersionTimestamp } from './documentVersioning';
import { generateVorankuendigungPDF } from './pdf/generatorVorank';
import { auftragFuerWiederholung, legeUnterlageAb, type AblageErgebnis, type UnterlageAblegen } from './unterlagenAblage';

/**
 * Anschluss der Vorankündigung an die zentrale Ablage (Stufe 1A, Entscheid E17).
 *
 * WANN abgelegt wird, entscheidet die Ansicht (`VorankuendigungView`); hier
 * steht, WAS abgelegt wird — Titel, Dateiname, Stand, Fassungskennung — und
 * der eine Weg dorthin. Die reinen Teile sind getestet; der Weg wirft nie.
 *
 * Zwei Anlässe:
 *   'versand' — der PDF-Export mit Leistungsbuchung. Das ist der Moment, in
 *               dem die Vorankündigung als fertig gilt und das Blatt an die
 *               Behörde geht. Ein Archiv-Schnappschuss entsteht dort ohnehin;
 *               seine Id ist die Fassungskennung.
 *   'entwurf' — nur per Knopf „Entwurf in Dokumente ablegen". Kennung ist ein
 *               Hash über den Inhalt des Blattes: derselbe Stand zweimal
 *               abgelegt ergibt `schon_abgelegt`, nicht zwei Zeilen; eine
 *               Änderung ergibt eine neue Zeile.
 *
 * Beide Kennungen sind stabil über jeden erneuten Versuch — deshalb kann
 * „Erneut ablegen" nach einer verlorenen Antwort nichts verdoppeln: die
 * Ablage findet die Fassung über `fassung_ref` wieder (Unique-Index).
 *
 * Der Auftrag (Blob + Kennung) wird EINMAL gebaut (`bereiteVorankAblageVor`)
 * und für den erneuten Versuch festgehalten (`legeVorankAuftragAb`): es geht
 * immer dasselbe Blatt hinaus, nicht ein frisch gesetztes.
 *
 * Vorschau, der Download einer Archiv-Version und „Version speichern" legen
 * NICHTS ab — sonst füllte sich die Ablage mit Zwischenständen (Plan 1A.3).
 *
 * Versand ist zugleich AUSGABE (Stufe 6.1/6.3): der Auftrag trägt dann
 * `ausgabe` mit den Projektdaten, und der Kern legt neben der Zeile den
 * Journal-Eintrag mit dem Steckbrief-Auszug an — der Anker, gegen den der
 * Änderungsdienst später vergleicht. Ein Entwurf ist keine Ausgabe und
 * bekommt keinen Anker: er löst keine Rückfrage aus.
 */

export type VorankAblageAnlass = 'versand' | 'entwurf';

/** Der Stand, der abgelegt wird — unabhängig davon, woher die Bytes kommen. */
export interface VorankFassung {
    anlass: VorankAblageAnlass;
    /** ISO-Zeitpunkt: Datum des Archiv-Schnappschusses bzw. des Klicks. */
    zeitpunkt: string;
    /** Id des Archiv-Schnappschusses — nur beim Versand. */
    archivId: string | null;
    /** Beschriftung des Schnappschusses („PDF-Export Erstellung") — nur beim Versand. */
    bezeichnung: string | null;
    /** Inhaltskennung des Blattes — nur beim Entwurf (siehe `vorankInhaltsHash`). */
    inhaltsHash: string | null;
}

/** Ergebnis der zentralen Ablage, so wie `legeUnterlageAb` es liefert. */
export type VorankAblageErgebnis = AblageErgebnis;
/** Der fertige Auftrag an die zentrale Ablage — Blob und Kennung inklusive. */
export type VorankAblageAuftrag = UnterlageAblegen;

/**
 * Felder, die NICHT auf das Blatt kommen und deshalb nicht in die
 * Inhaltskennung gehören: das Archiv (alle Vorgänger), der Verlauf
 * (Export-Zeitpunkte) und die Sperrmarken der Punkte 7–10. Sonst machte ein
 * Export oder ein Klick auf das Schloss aus demselben Blatt einen „neuen"
 * Entwurf.
 */
const NICHT_AUF_DEM_BLATT: ReadonlyArray<keyof VorankuendigungData> = ['archive', 'history', 'manualFields'];

/**
 * Kennung des Blattinhalts: Hash über alle Felder, die ins PDF gehen, mit
 * sortierten Schlüsseln (`stabilerInhaltsHash`) — die Reihenfolge der Felder
 * hängt davon ab, ob der Stand aus der Datenbank oder aus dem Editor kam.
 */
export const vorankInhaltsHash = (snapshot: VorankuendigungData): string => {
    const inhalt: Partial<VorankuendigungData> = { ...snapshot };
    NICHT_AUF_DEM_BLATT.forEach(feld => { delete inhalt[feld]; });
    return stabilerInhaltsHash(inhalt);
};

/** Fassung eines Export-Schnappschusses (Anlass Versand). */
export const fassungAusArchiv = (entry: VorankuendigungArchiveEntry): VorankFassung => ({
    anlass: 'versand',
    zeitpunkt: entry.date,
    archivId: (entry.id || '').trim() || null,
    bezeichnung: describeVorankuendigungVersion(entry),
    inhaltsHash: null,
});

/** Fassung eines von Hand abgelegten Entwurfs — die Kennung kommt aus dem Inhalt. */
export const entwurfsFassung = (jetztIso: string, snapshot: VorankuendigungData): VorankFassung => ({
    anlass: 'entwurf',
    zeitpunkt: jetztIso,
    archivId: null,
    bezeichnung: null,
    inhaltsHash: vorankInhaltsHash(snapshot),
});

const zweistellig = (n: number): string => String(n).padStart(2, '0');

/** „TT.MM.JJJJ" in Ortszeit — dieselbe Lesart wie der Zeitstempel der Versionen. */
export const datumKurz = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** „JJJJ-MM-TT" für Dateinamen — sortiert sich im Ordner von selbst. */
const datumFuerDatei = (d: Date): string =>
    `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;

/**
 * Anzeigename in der Ablage.
 *
 * Die Fassung trägt nur das Datum — so steht es auch auf dem Blatt. Der
 * Entwurf trägt zusätzlich die Uhrzeit, weil es an einem Tag mehrere geben
 * kann und sie sich sonst in der Liste nicht unterscheiden ließen. Das Wort
 * „Entwurf" steht hier bewusst NICHT: das Präfix „ENTWURF — " setzt die
 * zentrale Ablage für alle Module gleich (`unterlageTitel`), ein zweites
 * hier ergäbe „ENTWURF — Vorankündigung — Entwurf vom …".
 */
export const vorankAblageTitel = (f: VorankFassung): string =>
    f.anlass === 'versand'
        ? `Vorankündigung — Fassung vom ${datumKurz(f.zeitpunkt)}`
        : `Vorankündigung — Stand vom ${formatVersionTimestamp(f.zeitpunkt)}`;

/**
 * Dateiname ohne Umlaute, Leer- und Sonderzeichen (Regel wie
 * `planungIntegration.ts`): er wandert in E-Mail-Anhänge und Downloads, wo
 * ein Umlaut je nach Gegenstelle zu „Vorank�ndigung" wird.
 */
export const vorankAblageDateiname = (f: VorankFassung): string => {
    const d = new Date(f.zeitpunkt);
    if (Number.isNaN(d.getTime())) {
        return f.anlass === 'versand' ? 'Vorankuendigung_Fassung.pdf' : 'Vorankuendigung_Entwurf.pdf';
    }
    if (f.anlass === 'versand') return `Vorankuendigung_Fassung_${datumFuerDatei(d)}.pdf`;
    return `Vorankuendigung_Entwurf_${datumFuerDatei(d)}_${zweistellig(d.getHours())}${zweistellig(d.getMinutes())}.pdf`;
};

/**
 * Stabile Kennung des Standes für `fassung_ref`.
 *
 * Versand: die Archiv-Id des Schnappschusses — nicht der Zeitstempel, denn die
 * Id ist es, über die der Archiv-Reiter und später der Änderungsdienst (6.1)
 * diese Fassung wiederfinden. Fehlt sie (Altbestand ohne Id), bleibt der
 * Zeitstempel als zweitbester Anker.
 * Entwurf: der Inhaltshash — unverändert noch einmal abgelegt heißt
 * `schon_abgelegt`, nicht Dublette. Ein eigenes Präfix, damit Entwurf und
 * exportierte Fassung desselben Inhalts zwei Zeilen bleiben: das sind zwei
 * Anlässe, und der Export ist der Nachweis.
 */
export const vorankFassungRef = (f: VorankFassung): string | null => {
    if (f.anlass === 'versand') {
        const kennung = f.archivId || (f.zeitpunkt || '').trim();
        return kennung ? `vorank:${kennung}` : null;
    }
    const hash = (f.inhaltsHash || '').trim();
    return hash ? `vorank-entwurf:${hash}` : null;
};

/** Kurzer Stand für die Spalte „Stand" der Ablage. */
export const vorankAblageStand = (f: VorankFassung): string =>
    f.anlass === 'versand'
        ? datumKurz(f.zeitpunkt)
        : `Entwurf ${formatVersionTimestamp(f.zeitpunkt)}`;

/**
 * Beschreibung — sagt dem Leser in der Ablage, WIE die Zeile entstanden ist.
 * Den Satz „Entwurfsstand — nicht freigegeben." hängt die zentrale Ablage
 * beim Entwurf selbst an.
 */
export const vorankAblageBeschreibung = (f: VorankFassung): string =>
    f.anlass === 'versand'
        ? `${f.bezeichnung || 'PDF-Export'} am ${formatVersionTimestamp(f.zeitpunkt)} — beim Export automatisch abgelegt.`
        : `Von Hand abgelegt am ${formatVersionTimestamp(f.zeitpunkt)}; kein Export, keine Leistungsbuchung.`;

/**
 * Die Absage VOR jeder Ablage — oder `null`. Gilt für den Export wie für den
 * Knopf. (Grund oder `null` statt eines `{ erlaubt: boolean }`: das Projekt
 * übersetzt ohne `strictNullChecks`, und ein boolescher Diskriminator wird
 * dort nicht verengt — Muster `pruefeAblageAuftrag` im Kern.)
 *
 * Nein ohne gespeichertes Projekt ('temp-id' ist die Vorbelegung eines nie
 * gespeicherten Projekts, siehe DokumentePanel) — die Storage-Policy fände
 * kein Projekt. Nein im Lesemodus: der Export läuft dort zwar noch, aber die
 * Ablage ist ein Schreibvorgang am Projekt, und der Balken des Aufrufers
 * verspricht, dass keiner stattfindet. Der Grund wird angezeigt, nie
 * verschluckt.
 */
export const ablageAbsage = (
    a: { projectId: string | undefined | null; isReadOnly: boolean },
): string | null => {
    const id = (a.projectId || '').trim();
    if (!id || id === 'temp-id') {
        return 'Das Projekt ist noch nicht gespeichert — die Vorankündigung wurde nicht in Dokumente abgelegt.';
    }
    if (a.isReadOnly) {
        return 'Nur-Ansicht: die Vorankündigung wurde nicht in Dokumente abgelegt.';
    }
    return null;
};

/**
 * Der vollständige Auftrag an die zentrale Ablage — rein, ohne Bytes zu erzeugen.
 *
 * `metadata` sind die Projektdaten zum Zeitpunkt des Exports; sie gehen NUR
 * beim Versand als `ausgabe` mit (Steckbrief-Auszug fürs Journal). Der
 * Entwurf bekommt keine `ausgabe` — er ist keine Ausgabe (siehe Dateikopf).
 */
export const baueVorankAblageAuftrag = (
    projectId: string,
    datei: Blob,
    f: VorankFassung,
    metadata: Partial<ReportMetadata> | null = null,
): VorankAblageAuftrag => {
    const auftrag: VorankAblageAuftrag = {
        projectId,
        modul: 'vorankuendigung',
        datei,
        dateiname: vorankAblageDateiname(f),
        titel: vorankAblageTitel(f),
        stand: vorankAblageStand(f),
        fassungRef: vorankFassungRef(f),
        anlass: f.anlass,
        beschreibung: vorankAblageBeschreibung(f),
    };
    if (f.anlass === 'versand') {
        auftrag.ausgabe = { unterlage: 'vorankuendigung', metadata };
    }
    return auftrag;
};

export interface VorankAblageArgs {
    projectId: string;
    /** Der Stand, der auf das Blatt kommt: Archiv-Schnappschuss oder laufender Entwurf. */
    snapshot: VorankuendigungData;
    fassung: VorankFassung;
    /** Projektdaten für den Steckbrief-Auszug des Journals — nur beim Versand ausgewertet. */
    metadata?: Partial<ReportMetadata> | null;
}

export type VorankAblageVorbereitung =
    | { status: 'ok'; auftrag: VorankAblageAuftrag }
    | { status: 'fehler'; fehler: string };

/**
 * Setzt das Blatt aus dem Schnappschuss und baut den Auftrag. Wirft nie.
 *
 * Das Blatt wird hier NEU gesetzt und nicht vom Download übernommen: was in
 * der Ablage liegt, ist nachweislich der Schnappschuss — nicht ein Stand,
 * der im Editor zwischen Export und Ablage noch eine Taste weiter war. Der
 * Auftrag ist danach festgehalten; ein erneuter Versuch schickt ihn
 * unverändert (`legeVorankAuftragAb`).
 */
export const bereiteVorankAblageVor = (a: VorankAblageArgs): VorankAblageVorbereitung => {
    try {
        const datei = generateVorankuendigungPDF(a.snapshot).output('blob') as Blob;
        return { status: 'ok', auftrag: baueVorankAblageAuftrag(a.projectId, datei, a.fassung, a.metadata ?? null) };
    } catch (e) {
        return {
            status: 'fehler',
            fehler: `Das PDF konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}`,
        };
    }
};

/** Reicht einen vorbereiteten Auftrag an die zentrale Ablage — erster wie erneuter Versuch. Wirft nie. */
export const legeVorankAuftragAb = (auftrag: VorankAblageAuftrag): Promise<VorankAblageErgebnis> =>
    legeUnterlageAb(auftrag);

/**
 * Der Auftrag für „Erneut ablegen": derselbe Auftrag, mit dem Steckbrief-
 * Anker des ERSTEN Versuchs (Kern: `auftragFuerWiederholung`). So friert der
 * Wiederholungsversuch den Stand ein, mit dem das Blatt hinausging — nicht
 * den inzwischen geänderten. Hatte der erste Versuch keinen Anker, trägt der
 * Kern beim Wiederholen keinen nach und sagt das.
 */
export const vorankAuftragFuerWiederholung = (
    auftrag: VorankAblageAuftrag,
    ergebnis: VorankAblageErgebnis | null,
): VorankAblageAuftrag => auftragFuerWiederholung(auftrag, ergebnis);

/**
 * Der Warnsatz zu einem GELUNGENEN Ergebnis — oder `null`.
 *
 * `schon_abgelegt` ist kein uneingeschränkter Erfolg: der Kern meldet dort in
 * `fehler`/`verwaisteDatei`, wenn die doppelt hochgeladene Datei nicht wieder
 * aus dem Bucket kam und weiter Speicher belegt. Die Fassung liegt — aber
 * etwas gehört gemeldet, und zwar ohne „Erneut ablegen" (das legte dieselbe
 * Fassung nur noch einmal nicht ab). Bei `status: 'fehler'` ist der Satz
 * schon im Fehler enthalten; hier kommt dann `null`.
 */
export const ablageWarnung = (ergebnis: VorankAblageErgebnis): string | null => {
    if (ergebnis.status === 'fehler') return null;
    const satz = (ergebnis.fehler || '').trim();
    if (satz) return satz;
    const pfad = (ergebnis.verwaisteDatei || '').trim();
    return pfad
        ? `Eine dabei hochgeladene Datei konnte nicht wieder entfernt werden und belegt weiter Speicher (${pfad}). Bitte der Verwaltung melden.`
        : null;
};

/**
 * Die Hinweise des Kerns zu einem GELUNGENEN Ergebnis — etwa „Journal-Eintrag
 * nicht angelegt" (Stufe 6.3). Sie sind keine Fehler: die Fassung liegt, der
 * Export gilt. Angezeigt werden sie dezent, verschluckt nie. Bei `fehler`
 * steht alles schon im Fehlersatz; dann kommt `[]`.
 */
export const ablageHinweise = (ergebnis: VorankAblageErgebnis): string[] => {
    if (ergebnis.status === 'fehler') return [];
    return (ergebnis.hinweise || []).map(h => (h || '').trim()).filter(Boolean);
};
